import { arrayMoveImmutable } from 'array-move';
export function insertAtIndex<T>(
  array: T[],
  insertAt: number,
  item: T,
  withOrder = true
): T[] {
  const items = [...array];

  items.splice(insertAt, 0, item);
  if (withOrder)
    return items.map((i, order) => ({
      ...i,
      order,
    }));

  return items;
}

/**
 * Place a freshly duplicated section right after its original, mirroring what
 * the API did: the copy takes original.order + 1 and every later section moves
 * down one. The mutation returns the copy's PRE-shift order (the original's),
 * so using that as an array index put the copy in the wrong slot, and the
 * sidebar (which draws items[0] and the sorted middle separately) showed it
 * twice until the next page load. Unknown original (a branch child, not in
 * this top-level list): unchanged, the navigation that follows reloads it.
 */
export function insertDuplicate<
  T extends { id: string; order?: Maybe<number> },
>(items: T[], originalId: string, copy: T): T[] {
  const idx = items.findIndex(i => i.id === originalId);

  if (idx === -1) return items;

  const order = (items[idx].order ?? 0) + 1;
  const shifted = items.map(i =>
    (i.order ?? 0) >= order ? { ...i, order: (i.order ?? 0) + 1 } : i
  );

  return insertAtIndex(shifted, idx + 1, { ...copy, order }, false);
}

export function reorder<T>(
  items: T[],
  currentIdx: number,
  newIdx: number
): T[] {
  const newItems = arrayMoveImmutable(items, currentIdx, newIdx).map(
    (i, order) => ({
      ...i,
      order,
    })
  );

  return newItems;
}

// Comparators must return 0 for ties — a `>` ternary returns -1 for equal
// orders, which is inconsistent (compare(a,b) === compare(b,a) === -1) and
// makes V8 reverse tied items on every sort pass. Sorting in place on props
// then re-shuffles the list on each re-render.
export function sortByOrder<T extends { order?: Maybe<number> }>(
  items: readonly T[]
): T[] {
  return [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export function removeById<T extends { id: Maybe<string> }>(
  items: T[],
  id: string
): T[] {
  const newItems = [...items].filter(i => i.id !== id);

  return newItems;
}

export function updateById<T extends { id: Maybe<string> }>(
  items: T[],
  item: T
): T[] {
  const idx = items.findIndex(i => i.id === item.id);

  if (idx === -1) return items;
  const newItems = [...items];

  newItems[idx] = item;

  return newItems;
}
